import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';

/**
 * Typeahead combobox for the Service Request "Event Type" field.
 *
 * UI-only enhancement: same source-of-truth values, same save behavior.
 * Keyboard (arrow keys + Enter + Esc), mouse click, and typed filtering
 * are all provided by the underlying shadcn Command primitive.
 */
export function EventTypeCombobox({
  options,
  value,
  onChange,
  placeholder = 'Select event type',
  searchPlaceholder = 'Search event types…',
  emptyMessage = 'No event types match',
  testId = 'event-type-select',
  triggerId = 'event_type',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const safeOptions = Array.isArray(options) ? options : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${value ? 'text-[#323033]' : 'text-[#5A5A5A]'}`}
          data-testid={testId}
          disabled={disabled}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {safeOptions.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${opt}`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${opt === value ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="truncate">{opt}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default EventTypeCombobox;
